import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import history from "@history";
import { useTranslation } from "react-i18next";

import { Column } from "app/types/TableTypes";
import { AppState } from "app/store";

import PageHeader from "app/main/shared-components/page-header/PageHeader";
import BaseTable from "app/main/shared-components/base-table/BaseTable";
import PaginationBar from "app/main/shared-components/pagination-bar/PaginationBar";
import { toggleQuickPanel } from "app/main/shared-components/quick-panel/store";

import CouponsTabs from "./CouponsTabs";
import { fetchPromotionalCoupons } from "./store/promotional-coupons.slice";

type CouponRow = {
  id: string;
  code: string;
  description: string;
  active: boolean;
  discount: string;
  minPurchaseAmount: number;
  maxDiscountAmount: number;
  maxUses: number;
  usageLimitPerCustomer: number;
  usedCount: number;
  validFrom: string;
  validUntil: string;
  createdAt: string;
};

const PromotionalCouponsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const ts = (key: string, fallback: string) => String(t(key, fallback));

  const { page, loading } = useSelector(
    (state: AppState) => state.coupons.promotional
  );

  
  useEffect(() => {
    (dispatch as any)(fetchPromotionalCoupons({ page: 0, size: 20 }));
  }, [dispatch]);
    function formatDateDDMMYYYY(iso?: string | null): string {
    if (!iso) return "";

    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;

    const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  const data: CouponRow[] = useMemo(() => {
    if (!page || !page.content) return [];

  return page.content.map((c) => ({
    id: c.id,
    code: c.code,
    description: c.description ?? "",
    active: c.isActive,
    discount: `${c.discountPercentage}%`,
    minPurchaseAmount: c.minPurchaseAmount ?? 0,
    maxDiscountAmount: c.maxDiscountAmount ?? 0,
    maxUses: c.maxUses ?? 0,
    usageLimitPerCustomer: c.usageLimitPerCustomer ?? 0,
    usedCount: c.currentUses ?? 0,
    validFrom: formatDateDDMMYYYY(c.validFrom),
    validUntil: formatDateDDMMYYYY(c.validUntil),
    createdAt: formatDateDDMMYYYY(c.createdAt),
  }));
}, [page]);

  const columns: Column[] = useMemo(
    () => [
      {
        Header: ts("app.main.coupons.columns.code", "Coupon code"),
        accessor: "code",
      },
      {
        Header: ts(
          "app.main.coupons.columns.description",
          "Description"
        ),
        accessor: "description",
      },
      {
        Header: ts("app.main.coupons.columns.active", "Active"),
        accessor: "active",
        Cell: ({ value }: any) =>
          value ? ts("Yes", "Yes") : ts("No", "No"),
      },
      {
        Header: ts("app.main.coupons.columns.discount", "Discount"),
        accessor: "discount",
      },
      // {
      //   Header: ts(
      //     "app.main.coupons.columns.minPurchaseAmount",
      //     "Min purchase amount"
      //   ),
      //   accessor: "minPurchaseAmount",
      // },
      // {
      //   Header: ts(
      //     "app.main.coupons.columns.maxDiscountAmount",
      //     "Max discount amount"
      //   ),
      //   accessor: "maxDiscountAmount",
      // },
      {
        Header: ts("app.main.coupons.columns.maxUses", "Max uses"),
        accessor: "maxUses",
      },
      {
        Header: ts(
          "app.main.coupons.columns.usageLimitPerCustomer",
          "Max uses per customer"
        ),
        accessor: "usageLimitPerCustomer",
      },
      {
        Header: ts("app.main.coupons.columns.usedCount", "Used"),
        accessor: "usedCount",
      },
      {
        Header: ts("app.main.coupons.columns.validFrom", "Valid from"),
        accessor: "validFrom",
      },
      {
        Header: ts("app.main.coupons.columns.validUntil", "Valid until"),
        accessor: "validUntil",
      },
      {
        Header: ts("app.main.coupons.columns.createdAt", "Created"),
        accessor: "createdAt",
      },
    ],
    [t]
  );

  // ---- pagination info (copy the SalesList pattern) ----
  const pageSize = page?.size || 20;
  const totalPages = page?.totalPages || 1;
  const currentPageZeroBased = page?.number ?? 0;

  return (
    <div>
      <PageHeader
        title={String(t("app.main.coupons.promotional.title", "Coupons"))}
        desc={String(
          t(
            "app.main.coupons.promotional.desc",
            "Create promotional discount coupons and manage existing ones."
          )
        )}
        button={String(
          t("app.main.coupons.promotional.new", "New coupon")
        )}
        onClickButton={() => {
          history.push("/coupons/promotional/create");
          dispatch(toggleQuickPanel());
        }}
      />

      <CouponsTabs />

      <div style={{ marginTop: 16 }}>
        <BaseTable columns={columns} data={data} isLoading={loading} />
        <PaginationBar
          totalPages={totalPages}
          currentPage={currentPageZeroBased + 1}
          onPageChange={(pageZeroBased: number) => {
            const newPage = pageZeroBased; 
            (dispatch as any)(
              fetchPromotionalCoupons({
                page: newPage,
                size: pageSize,
              })
            );
          }}
        />
      </div>
    </div>
  );
};

export default PromotionalCouponsPage;
